import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from './lib/api.js';
import { ensureServiceWorker, pushSupported } from './lib/push.js';
import PushPrompt from './components/PushPrompt.jsx';

// Primary tabs are always visible. Secondary tabs hide behind a "More ▾"
// dropdown so the nav doesn't overflow on phones — Reports + Reconcile
// stay above the fold where they belong.
// Primary = always-visible nav. Trimmed to fit a phone screen without
// horizontal scroll: the daily-use four plus Reports + Reconcile (owner).
// Equipment/Transactions/etc. drop into the More dropdown.
const PRIMARY_TABS = [
  { to: '/my-day',    label: 'My Day' },
  { to: '/income',    label: 'Income' },
  { to: '/receipts',  label: 'Receipts' },
  { to: '/reports',   label: 'Reports' },
  { to: '/reconcile', label: 'Reconcile', ownerOnly: true },
];

const SECONDARY_TABS = [
  { to: '/equipment',      label: 'Equipment' },
  { to: '/calendar',       label: 'Service calendar' },
  { to: '/transactions',   label: 'Transactions' },
  { to: '/cattle',         label: 'Cattle' },
  { to: '/chickens',       label: 'Chickens' },
  { to: '/chores',         label: 'Chores' },
  { to: '/projects',       label: 'Projects' },
  { to: '/reimbursements', label: 'Reimbursements' },
  { to: '/contractors',    label: 'Contractors', ownerOnly: true },
];

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/auth/me')
      .then(({ user }) => {
        setUser(user);
        // Register service worker so push subscription works as soon as user enables it
        if (pushSupported()) ensureServiceWorker().catch(e => console.warn('SW register failed:', e));
      })
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Splash />;
  if (!user) return null;

  const isOwner = user.role === 'owner';
  // Owners, plus hands granted the equipment-manager permission
  const canManageEquipment = isOwner || !!user.can_manage_equipment;

  async function logout() {
    await api.post('/api/auth/logout');
    navigate('/login');
  }

  const visibleSecondary = SECONDARY_TABS.filter(t => !t.ownerOnly || isOwner);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-olive-900 text-cream-50 shadow">
        <div className="mx-auto max-w-5xl px-4 py-3 flex items-center gap-3">
          <img src="/logo.svg" alt="" className="h-8 w-8 bg-cream-50 rounded-md p-0.5" />
          <span className="font-serif text-xl tracking-wide">KingStead</span>
          <span className="text-cream-100/60 text-sm hidden sm:inline">· Farms</span>
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className="text-cream-100/80 hidden sm:inline">{user.name}</span>
            <span className="pill bg-gold-500/20 text-gold-500">{user.role}</span>
            <Link to="/settings" title="Settings" className="ml-2 text-cream-100/80 hover:text-cream-50 text-base">⚙</Link>
            <button onClick={logout} className="ml-2 text-cream-100/80 hover:text-cream-50 underline-offset-2 hover:underline text-sm">Sign out</button>
          </div>
        </div>
      </header>

      <nav className="bg-olive-700 text-cream-50 sticky top-0 z-10">
        {/* Outer container holds the More menu separately so its dropdown panel
            isn't clipped by the inner container's overflow-x-auto. */}
        <div className="mx-auto max-w-5xl px-2 flex items-stretch">
          <div className="flex overflow-x-auto items-stretch flex-1 min-w-0">
            {PRIMARY_TABS
              .filter(t => !t.ownerOnly || isOwner)
              .map(t => <Tab key={t.to} to={t.to}>{t.label}</Tab>)}
          </div>
          {visibleSecondary.length > 0 && <MoreMenu tabs={visibleSecondary} />}
        </div>
      </nav>

      <main className="mx-auto max-w-5xl w-full px-4 py-6 flex-1">
        <PushPrompt />
        <Outlet context={{ user, isOwner, canManageEquipment }} />
      </main>

      <footer className="text-center text-xs text-ink-700/60 py-4 border-t border-olive-100/60">
        KingStead Farms · v0.1 · {user.role === 'owner' ? 'Owner view' : 'Hand view'}
      </footer>
    </div>
  );
}

function Tab({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition ${
          isActive ? 'border-gold-500 text-cream-50' : 'border-transparent text-cream-50/70 hover:text-cream-50'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

// Dropdown holding the less-frequent tabs so the nav stays under control.
// Highlights gold when one of its routes is active.
function MoreMenu({ tabs }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const activeInside = tabs.some(t => location.pathname.startsWith(t.to));

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  return (
    <div ref={ref} className="relative flex items-stretch">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition ${
          activeInside ? 'border-gold-500 text-cream-50' : 'border-transparent text-cream-50/70 hover:text-cream-50'
        }`}
      >
        More <span className={`inline-block transition ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div role="menu"
             className="absolute right-0 top-full mt-0 min-w-[10rem] bg-olive-900 ring-1 ring-olive-700 shadow-lg rounded-b-md py-1 z-20">
          {tabs.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              role="menuitem"
              className={({ isActive }) =>
                `block px-4 py-2 text-sm whitespace-nowrap ${
                  isActive ? 'text-gold-500 bg-olive-700/40' : 'text-cream-50/85 hover:bg-olive-700/40 hover:text-cream-50'
                }`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50">
      <div className="text-olive-700 font-serif text-2xl">KingStead Farms</div>
    </div>
  );
}
